/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';
import useRouter from 'js/lib/useRouter';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { type EventData, useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import SectionHeadingComponent from 'bundles/browse-common/components/SectionHeadingComponent';
import type { PartnerData } from 'bundles/browse-common/utils/experimentUtils';

import _t from 'i18n!nls/search-common';

type PartnerCardProps = {
  partner: PartnerData;
  partnerIndex: number;
  loading?: 'eager' | 'lazy';
};
type PartnerListProps = { partners: PartnerData[]; loading?: 'eager' | 'lazy' };

const styles = {
  componentContainer: css`
    ${breakpoints.up('md')} {
      padding: var(--cds-spacing-200) 0;
    }
    ${breakpoints.down('md')} {
      padding: var(--cds-spacing-150) 0;
    }
  `,
  partnerListContainer: css`
    list-style: none;
    padding-left: var(--cds-spacing-50);
    padding-right: var(--cds-spacing-50);
    margin: 0;
    display: flex;
    overflow-x: auto;
    gap: var(--cds-spacing-150);
    scrollbar-width: none;
    -ms-overflow-style: none;
    justify-content: space-between;
    width: 100%;

    &::-webkit-scrollbar {
      display: none;
    }

    ${breakpoints.up('md')} {
      gap: var(--cds-spacing-100);
    }
  `,

  partnerCard: css`
    margin: 0 !important;
    margin-top: var(--cds-spacing-100) !important;
    border-radius: var(--cds-border-radius-100);
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    width: 147px;
    height: 97px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    flex-shrink: 0;
    transition: all 0.3s ease;
    transform-origin: center;

    :hover,
    :focus-visible {
      background-color: var(--cds-color-lighten-0);
      transform: scale(1.05);
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      overflow: hidden;
    }

    :focus-visible {
      outline: 1px solid var(--cds-color-purple-800) !important;
      outline-offset: 2px !important;
    }
  `,
  partnerTitle: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  partnerItem: css`
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
};

const PartnerCard = ({ partner, partnerIndex, loading }: PartnerCardProps) => {
  const track = useTracker();
  const router = useRouter();
  const partnerCardTrackingData: EventData<'view_partner_card'> = {
    partnerName: partner.partnerName,
    partnerCardIndex: partnerIndex,
  };

  const visibilityRef = useVisibilityTracker<HTMLDivElement>('view_partner_card', partnerCardTrackingData, [], {
    fullyVisible: true,
  });

  const onClickToTrack = () => {
    track('click_partner_card', partnerCardTrackingData);
    const url = `/search?query=${partner.relationSlug}`;
    router.push(url);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div ref={visibilityRef}>
      <Button css={styles.partnerCard} onClick={onClickToTrack} variant="ghost" aria-label={partner.partnerName}>
        <Imgix src={partner.image.url} alt={partner.imageAltText} loading={loading} />
      </Button>
    </div>
  );
};

const PartnerList = (props: PartnerListProps) => {
  const { partners, loading = 'lazy' } = props;
  return (
    <div css={styles.componentContainer}>
      <SectionHeadingComponent>{_t('Similar partners you may want to explore')}</SectionHeadingComponent>
      <div css={styles.partnerListContainer}>
        {partners.map((partner, index) => (
          <div key={partner.relationSlug} css={styles.partnerItem}>
            <PartnerCard partnerIndex={index} partner={partner} loading={loading} />
            <Typography2 component="h4" variant="subtitleSmall">
              {partner.partnerName}
            </Typography2>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PartnerList;
